/* eslint-disable react/require-default-props */
import React from 'react';
import AiLoadingText from 'components/atoms/ai_loading_text';
import AiManagementProcessingCard from 'components/atoms/ai_management_processing_card';
import AiManagementSectionTitle from 'components/atoms/ai_management_section_title';
import AiManagementStatCard from 'components/atoms/ai_management_stat_card';
import AiManagementSummaryCard from 'components/atoms/ai_management_summary_card';
import AiManagementCompetitorExperience from 'components/molecules/ai_management_competitor_experience';
import AiManagementCompetitorPenalties from 'components/molecules/ai_management_competitor_penalties';
import AiManagementCompetitorPricing from 'components/molecules/ai_management_competitor_pricing';
import AiManagementCompetitorQualification from 'components/molecules/ai_management_competitor_qualification';
import AiManagementCompetitorSize from 'components/molecules/ai_management_competitor_size';
import { usePossibleCompetitorAnalysis } from 'hooks/usePossibleCompetitorAnalysis';
import {
  PossibleCompetitorAnalysis,
  PossibleCompetitorSize,
} from 'services/actions/ai_management_possible_competitors';
import { count, percent, plural } from 'services/ai_management_format';
import { stateName } from 'services/ai_management_possible_competitors';
import { Section } from './styles';

interface AiManagementCompetitorAnalysisProps {
  biddingId: number | null;
  cnpj: string;
  className?: string;
  onOpenContracts?: (size: PossibleCompetitorSize) => void;
}

const LOADING_MESSAGES = [
  'Analisando o histórico da empresa',
  'Reunindo desclassificações e inabilitações',
  'Quase lá',
];

const summaryBadges = (analysis: PossibleCompetitorAnalysis) => {
  const states = analysis.por_uf.dados.length;
  const publicBodies = analysis.por_orgao.dados.filter((orgao) => orgao.adjudicados > 0).length;
  return [
    analysis.me_epp ? 'ME/EPP' : null,
    states > 0 ? `Atua em ${plural(states, 'estado', 'estados')}` : null,
    publicBodies > 0 ? `Venceu em ${plural(publicBodies, 'órgão', 'órgãos')}` : null,
  ].filter((badge): badge is string => Boolean(badge));
};

const stats = (analysis: PossibleCompetitorAnalysis) => [
  { label: 'Licitações', value: count(analysis.resumo.licitacoes) },
  { label: 'Itens adjudicados', value: count(analysis.resumo.adjudicados) },
  { label: 'Taxa de adjudicação', value: percent(analysis.resumo.taxa_adjudicacao) },
  { label: 'Itens desclassificados', value: count(analysis.resumo.desclassificados) },
  { label: 'Itens inabilitados', value: count(analysis.resumo.inabilitados) },
];

const points = (analysis: PossibleCompetitorAnalysis) => [
  {
    key: 'strengths',
    title: 'Pontos fortes',
    icon: 'check',
    iconClass: 'material-icons',
    tone: 'text-success',
    items: analysis.analise?.pontos_fortes ?? [],
    empty: 'Nenhum ponto forte identificado.',
  },
  {
    key: 'weaknesses',
    title: 'Pontos fracos',
    icon: 'report_problem',
    iconClass: 'material-icons-outlined',
    tone: 'text-danger',
    items: analysis.analise?.pontos_de_atencao ?? [],
    empty: 'Nenhum ponto fraco identificado.',
  },
];

const AiManagementCompetitorAnalysis: React.FC<AiManagementCompetitorAnalysisProps> = ({
  biddingId,
  cnpj,
  className = '',
  onOpenContracts,
}) => {
  const { analysis, pending, loading, error } = usePossibleCompetitorAnalysis(biddingId, cnpj);

  const renderAnalysis = (data: PossibleCompetitorAnalysis) => (
    <>
      {data.analise?.resumo_geral ? (
        <AiManagementSummaryCard badges={summaryBadges(data)} className="mb-3">
          {data.analise.resumo_geral}
        </AiManagementSummaryCard>
      ) : null}

      <div className="stats-grid mb-3">
        {stats(data).map((stat) => (
          <AiManagementStatCard key={stat.label} value={stat.value} label={stat.label} />
        ))}
      </div>

      {data.analise ? (
        <div className="row mb-3">
          {points(data).map((group) => (
            <div key={group.key} className="col-md-6 mb-3 mb-md-0">
              <div className="points-card border h-100 px-3 py-3">
                <div
                  className={`points-title d-flex align-items-center font-weight-bold mb-2 ${group.tone}`}
                >
                  <i className={`${group.iconClass} mr-2`}>{group.icon}</i>
                  {group.title}
                </div>
                {group.items.length ? (
                  <ul className="points-list pl-3 mb-0">
                    {group.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-muted">{group.empty}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <AiManagementCompetitorQualification qualification={data.habilitacao} className="mb-3" />
      <AiManagementCompetitorPricing pricing={data.preco_competitivo} className="mb-3" />
      <AiManagementCompetitorExperience
        title="Experiência por órgão"
        columnLabel="Órgão"
        summary={data.por_orgao.texto}
        rows={data.por_orgao.dados.map((orgao) => ({
          ...orgao,
          key: String(orgao.id ?? orgao.nome),
          label: orgao.nome,
        }))}
        className="mb-3"
      />
      <AiManagementCompetitorExperience
        title="Experiência por estado"
        columnLabel="Estado"
        summary={data.por_uf.texto}
        rows={data.por_uf.dados.map((estado) => ({
          ...estado,
          key: estado.uf,
          label: stateName(estado.uf),
        }))}
        className="mb-3"
      />
      <AiManagementCompetitorSize
        size={data.porte}
        className="mb-3"
        onOpenContracts={onOpenContracts ? () => onOpenContracts(data.porte) : undefined}
      />
    </>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <AiManagementProcessingCard>
          <AiLoadingText messages={LOADING_MESSAGES} className="analysis-loading" />
        </AiManagementProcessingCard>
      );
    }
    if (error) {
      return (
        <div className="alert alert-warning mb-0" role="alert">
          Não foi possível carregar a análise desta empresa.
        </div>
      );
    }
    if (pending) {
      return (
        <AiManagementProcessingCard>
          Estamos processando a sua análise, volte em alguns minutos.
        </AiManagementProcessingCard>
      );
    }
    if (!analysis) {
      return <div className="text-muted">Ainda não há análise disponível para esta empresa.</div>;
    }
    return renderAnalysis(analysis);
  };

  return (
    <Section className={className}>
      <AiManagementSectionTitle className="mb-1">Análise da Empresa</AiManagementSectionTitle>
      <div className="text-muted mb-3">
        Dados com base no histórico geral da empresa no mercado de licitações.
      </div>
      {renderBody()}
      <AiManagementCompetitorPenalties biddingId={biddingId} cnpj={cnpj} className="mt-3" />
    </Section>
  );
};

export default AiManagementCompetitorAnalysis;
